import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/Badges";
import LevelBadge from "@/components/LevelBadge";
import Seal from "@/components/Seal";
import { certApi } from "@/lib/api";
import { Download, Loader2, ShieldCheck } from "lucide-react";

const CertificateView = () => {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    certApi.get(certId).then(({ data }) => setCert(data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [certId]);

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-wgreen-600" /></div>;
  if (error || !cert) return <div className="wechr-container py-20 text-center"><p className="text-navy-900">Certificate not found.</p></div>;

  return (
    <div className="wechr-section">
      <div className="wechr-container max-w-4xl">
        <Card className="overflow-hidden">
          <div className="border-b-4 border-gold-500 bg-navy-900 p-8 text-center text-white">
            <div className="flex items-center justify-center gap-3"><Seal size={40} /><span className="font-heading text-2xl font-bold">WECH-RB</span></div>
            <p className="mt-1 text-sm text-[#B9C4D6]">We Create Harmony with Responsible Businesses</p>
          </div>
          <CardContent className="p-8 sm:p-12">
            <div className="flex flex-col items-center text-center">
              <h1 className="font-heading text-3xl font-semibold text-navy-900">WECH-RB DD Certification</h1>
              <p className="mt-1 text-sm italic text-[var(--wechr-muted-text)]">Independent Document-Reviewed Certification</p>
              <div className="my-8"><LevelBadge level={cert.level} size={140} /></div>
              <p className="text-sm text-[var(--wechr-muted-text)]">This certificate is proudly awarded to</p>
              <h2 className="mt-1 font-heading text-2xl font-semibold text-navy-900">{cert.company_name}</h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-navy-800">
                {cert.scope_type === "full"
                  ? `This certificate confirms that the company completed WECH-RB's independent, document-based certification review across all ${(cert.category_scores || []).length} WECH-RB assessment categories.`
                  : `This certificate confirms that the company completed WECH-RB's independent, document-based certification review for the selected WECH-RB assessment ${(cert.category_scores || []).length === 1 ? "category" : "categories"} listed below.`}
              </p>
              <div className="mt-6"><StatusBadge status={cert.effective_status} /></div>
            </div>

            <div className="mt-10 grid gap-4 border-t border-[var(--wechr-border)] pt-6 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <Detail label="Certificate ID" value={<span className="font-mono">{cert.certificate_id}</span>} />
              <Detail label="Overall Score" value={`${cert.overall_score}/100`} />
              <Detail label="Certification Level" value={cert.level} />
              <Detail label="Issue Date" value={cert.issue_date} />
              <Detail label="Expiration Date" value={cert.expiry_date} />
              <Detail label="Issued By" value={cert.reviewer} />
              <div className="sm:col-span-2 lg:col-span-3"><Detail label="Assessment Scope" value={cert.scope} /></div>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href={certApi.pdfUrl(cert.certificate_id)} target="_blank" rel="noreferrer" data-testid="certificate-download-button">
                <Button className="btn-green"><Download className="mr-2 h-4 w-4" /> Download certificate (PDF)</Button>
              </a>
              <a href={`/verify?id=${cert.certificate_id}`}><Button variant="outline"><ShieldCheck className="mr-2 h-4 w-4" /> Public verification</Button></a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div><p className="text-xs font-semibold uppercase tracking-wide text-[var(--wechr-muted-text)]">{label}</p><p className="mt-0.5 font-medium text-navy-900">{value}</p></div>
);

export default CertificateView;
